import { Form, Formik } from 'formik';
import CustomDatePicker from '../../../components/date-picker/customDatePicker';
import InputText from '../../../components/input-text/inputText';
import { type ModalStatusType, type OptionType } from '../../../types/common';
import ModalComponent from '../../../components/modal/modal';
import ModalFooterComponent from '../../../components/modal/modalFooterComponent';
import ModalBodyComponent from '../../../components/modal/modalBodyComponent';
import FormSelect from '../../../components/dropdown/formSelect';
import {
  useAdvanceBoolean,
  useGetGrantAcceptanceTemplateStatus,
  useGetGrantAcceptanceTemplateVariables,
  usePostGrantAcceptanceTemplate,
  useWealthlaneAwardApi,
} from '../../../hooks';
import _, { isEmpty } from 'lodash';
import { GRANT_ACCEPTANCE_TEMPLATE_VALIDATION } from '../../../utils/validations/validations';
import { Label } from '../../../components/input-text/input.styled';
import JoditRichTextEditor, {
  EditorRef,
} from '../../../components/rich-text-editor/joditRichTextEditor';
import { useRef, useState } from 'react';
import HtmlPreviewer from '../../../components/rich-text-editor/htmlPreviewer';
import AlertPopup from '../../../components/alert-popup';
import { GrantAcceptanceTemplateService } from '../../../services/wealthlane-award-services';
import { toast } from 'react-toastify';
import ToastComponent from '../../../components/toast';
import { useQueryClient } from 'react-query';
import Button from '../../../components/button';
import useInstance from '../../../hooks/custom-hooks/useInstance';

interface GrantAcceptanceTemplateModalProps {
  isModalOpen: boolean;
  closeModal: () => void;
  modalStatus: ModalStatusType;
  setModalStatus: (value: ModalStatusType) => void;
  loading: boolean;
  initialData: any;
}

const GrantAcceptanceTemplateModal = ({
  isModalOpen,
  closeModal,
  modalStatus,
  setModalStatus,
  loading,
  initialData,
}: GrantAcceptanceTemplateModalProps): JSX.Element => {
  const { handleRequest } = useWealthlaneAwardApi();
  const queryClient = useQueryClient();
  const { data: VariablesList } = useGetGrantAcceptanceTemplateVariables(handleRequest);
  const editorRef = useRef<EditorRef>(null);
  const {
    value: isConfirmOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
    customValue,
    setCustomValue,
  } = useAdvanceBoolean<string>(false);
  const [modalLoading, setModalLoading] = useState(false);
  const { data: GrantAcceptanceTemplateStatus, isLoading: isGrantAcceptanceTemplateStatusLoading } =
    useGetGrantAcceptanceTemplateStatus(handleRequest);
  const { mutate, isLoading } = usePostGrantAcceptanceTemplate(
    handleRequest,
    closeModal,
    modalStatus
  );
  const handleSubmit = (values: any): void => {
    mutate(values);
  };

  const todayDate = new Date();
  todayDate.setDate(todayDate.getDate() - 1);

  const variables = VariablesList?.map((item) => item.replace(/^\{\{|\}\}$/g, ''));

  const onPrimaryClick = (values?: any): void => {
    cloneGrantAcceptanceTemplate(values ? values : customValue);
  };

  const cloneGrantAcceptanceTemplate = (values: any): void => {
    setModalLoading(true);
    handleRequest(
      GrantAcceptanceTemplateService.postApiAwardApiAppGrantAcceptanceTemplateCloneContent(values)
    )
      .then((data: any) => {
        if (data != null) {
          toast.success(
            <ToastComponent label={'Success'} message={'Grant Acceptance Template Copied.'} />
          );
          void queryClient.invalidateQueries('getGrantAcceptanceTemplateList');
          setModalLoading(false);
          closeConfirmModal();
          closeModal();
        }
      })
      .catch(() => {
        setModalLoading(false);
        closeConfirmModal();
      })
      .finally(() => {
        setModalLoading(false);
        void queryClient.invalidateQueries('getGrantAcceptanceTemplateList');
        void queryClient.invalidateQueries('getGrantAcceptanceTemplateOptions');
        closeConfirmModal();
      });
  };

  const { getCurrentCid } = useInstance();
  const cid = getCurrentCid();

  return (
    <Formik
      enableReinitialize
      initialValues={
        modalStatus === 'Add Versions'
          ? { ...initialData, content: '', effectiveDate: '' }
          : initialData
      }
      validationSchema={GRANT_ACCEPTANCE_TEMPLATE_VALIDATION}
      onSubmit={(values) => {
        if (modalStatus === 'Clone') {
          if (values.templateName === initialData.templateName) {
            openConfirmModal();
            setCustomValue(values);
          } else {
            onPrimaryClick(values);
          }
        } else {
          handleSubmit(values);
        }
      }}>
      {({ touched, handleChange, errors, setFieldValue, setFieldTouched, values }) => {
        return (
          <ModalComponent
            show={isModalOpen}
            closeModal={closeModal}
            loading={loading}
            size='full'
            title={`${
              modalStatus === 'Add' ? 'Create' : modalStatus === 'Clone' ? 'Copy' : modalStatus
            } Template`}
            valueEdited={_.isEqual(values, initialData)}
            children={
              <Form>
                <ModalBodyComponent>
                  <div className='grid grid-cols-4 gap-x-8 mt-6'>
                    <InputText
                      type='text'
                      label='Template Name'
                      name='templateName'
                      className='col-span-2'
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                      inputType={initialData.modalStatus}
                      disabled={
                        modalStatus === 'View' ||
                        modalStatus === 'Edit' ||
                        modalStatus === 'Add Versions'
                      }
                      required
                      id='templateNameInput'
                    />
                    <FormSelect
                      label='Status'
                      name='statusId'
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                      options={GrantAcceptanceTemplateStatus}
                      values={values}
                      setFieldValue={setFieldValue}
                      inputType={modalStatus}
                      disabled={
                        modalStatus === 'View' ||
                        modalStatus === 'Edit' ||
                        modalStatus === 'Add Versions'
                      }
                      required
                      isLoading={isGrantAcceptanceTemplateStatusLoading}
                      id='statusIdInput'
                    />
                    <CustomDatePicker
                      label='Effective Date'
                      name={`effectiveDate`}
                      id='effectiveDate'
                      onChange={handleChange}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                      errors={errors}
                      touched={touched}
                      values={values}
                      errorValue={errors?.effectiveDate}
                      touchedValue={touched?.effectiveDate}
                      defaultValue={new Date()}
                      disabled={modalStatus === 'View'}
                      fieldLabelName='test'
                      required
                      width='100%'
                    />
                  </div>
                  <div>
                    <Button
                      variant={'primary'}
                      as='View'
                      title={'Preview'}
                      icon={'preview'}
                      id='previewButton'
                      type='button'
                      onClick={() => {
                        if (values?.content) {
                          window.sessionStorage.removeItem(
                            'grantAcceptanceTemplatePreviewContentId'
                          );
                          window.sessionStorage.setItem(
                            'grantAcceptanceTemplatePreviewContent',
                            values.content
                          );
                          window.open(
                            `/settings/organization-settings/grant-acceptance-template/preview/${
                              cid ? `#cid=${cid}` : ''
                            }`,
                            '_blank'
                          );
                        }
                      }}
                    />
                  </div>

                  <div className='grid sm:grid-cols-12 gap-x-8 mt-6'>
                    <div className='sm:col-span-12 relative'>
                      <Label className={`label-view`}>Editor</Label>
                      <JoditRichTextEditor
                        ref={editorRef}
                        name='content'
                        onChange={(value) => setFieldValue('content', value)}
                        value={values?.content != null ? values?.content : ''}
                        error={errors?.content}
                        touched={touched?.content}
                        variables={variables}
                        disabled={modalStatus === 'View'}
                      />
                    </div>

                    {/* <div className='sm:col-span-6'>
                      <Label className={`label-view`}>Preview</Label>
                      <HtmlPreviewer html={values?.content} />
                    </div> */}
                    {isConfirmOpen && (
                      <AlertPopup
                        isShow={isConfirmOpen}
                        closeModal={closeConfirmModal}
                        modalStatus={modalStatus}
                        customTitle={
                          'Please update the template name to create a new template. If you do not change the template name a new version under same template will be created. Are you sure you want to create a new version?'
                        }
                        handleSubmit={onPrimaryClick}
                        loading={modalLoading}
                      />
                    )}
                  </div>
                </ModalBodyComponent>
                <ModalFooterComponent
                  modalStatus={
                    modalStatus === 'Add Versions' || modalStatus === 'Clone' ? 'Edit' : modalStatus
                  }
                  loading={isLoading}
                  onCloseModal={() => {
                    closeModal();
                  }}
                  onEditModal={() => {
                    modalStatus === 'Clone' ? setModalStatus('Clone') : setModalStatus('Edit');
                  }}
                  valueChanged={_.isEqual(values, initialData)}
                />
              </Form>
            }
          />
        );
      }}
    </Formik>
  );
};

export default GrantAcceptanceTemplateModal;
